import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconArmchair as Armchair, IconArrowLeft as ArrowLeft, IconCircleDot as CircleDot, IconDoorEnter as DoorOpen, IconDownload as Download, IconGrid3x3 as Grid3x3, IconHandMove as Hand, IconKeyboard as Keyboard, IconLayoutBoard as LayoutTemplate, IconLoader2 as Loader2, IconMagnet as Magnet, IconPointer as MousePointer2, IconPentagon as Pentagon, IconStack2 as Layers, IconLock as Lock, IconArrowForwardUp as Redo2, IconLayoutRows as Rows3, IconDeviceFloppy as Save, IconAdjustmentsHorizontal as SlidersHorizontal, IconMasksTheater as Theater, IconTypography as Type, IconArrowBackUp as Undo2, IconUpload as Upload } from '@tabler/icons-react';
import Canvas, { type CanvasHandle } from './Canvas';
import Inspector from './Inspector';
import LayersPanel from './LayersPanel';
import TemplatesModal from './TemplatesModal';
import type { CreateRequest } from './AddPanel';
import {
    add, boundsOf, buildIndex, emptyPlan, makeLabel, nextNumericLabel, normalizePlan, SEAT_RADIUS, seatAbs, seatKey,
    shapeBounds, shapeKey, snapPoint, type Rect,
} from './geometry';
import {
    addRows, addShape, buildRow, createTable, deleteSelection, linkTables, moveSelection, newShape,
    pasteSelection, polygonShape, reshapeTable, rotateSelection, scaleShape, setSeatCategory, updateShape,
} from './planOps';
import {
    createGaArea, createObject, createSectionZone, defaultStartFor, duplicateSections, ensureFreeZone, findFreeOffset, fullZoneIndexes,
    nextItemLabel, zoneBounds, zoneSeatKeys,
} from './sections';
import { usePlanHistory } from './usePlanHistory';
import { addToast } from '@heroui/toast';
import type { EditorSettings, Plan, PlanIndex, Point, Selection, ShapeKind, Tool, Zone } from './types';
import { DialogProvider, useDialogs } from './dialogs';
import { cx, Divider, IconButton } from './ui';
import useIsMobile from '@/hooks/useIsMobile';

export type SeatMapStudioProps = {
    initialPlan?: unknown;
    onSave?: (plan: Plan) => void | Promise<unknown>;
    onBack?: () => void;
    title?: string;
    subtitle?: string;
    showExport?: boolean;
    lockedSeatsMessage?: string;
    editingLockedMessage?: string;
};

const SETTINGS_KEY = 'seatmap-studio-settings';
const DEFAULT_SETTINGS: EditorSettings = {
    seatSpacing: 25,
    rowSpacing: 30,
    gridSize: 5,
    showGrid: true,
    snap: true,
    showSeatNumbers: true,
    rowScheme: 'upper',
    seatScheme: 'numeric',
    tableSeats: 8,
    tableShape: 'round',
    tableEndSeats: 2,
};

const loadSettings = (): EditorSettings => {
    try {
        const raw = localStorage.getItem(SETTINGS_KEY);
        return raw ? { ...DEFAULT_SETTINGS, ...JSON.parse(raw) } : DEFAULT_SETTINGS;
    } catch {
        return DEFAULT_SETTINGS;
    }
};

const EMPTY: Selection = { seats: new Set(), shapes: new Set() };

const TOOLS: { id: Tool; label: string; key: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { id: 'select', label: 'Select', key: 'V', icon: MousePointer2 },
    { id: 'pan', label: 'Pan', key: 'H', icon: Hand },
    { id: 'row', label: 'Draw row', key: 'R', icon: Rows3 },
    { id: 'block', label: 'Seat block', key: 'B', icon: Grid3x3 },
    { id: 'seat', label: 'Single seat', key: 'S', icon: Armchair },
    { id: 'table', label: 'Table', key: 'C', icon: CircleDot },
    { id: 'stage', label: 'Stage', key: 'T', icon: Theater },
    { id: 'polygon', label: 'Area (polygon)', key: 'P', icon: Pentagon },
    { id: 'entrance', label: 'Entrance', key: 'E', icon: DoorOpen },
    { id: 'text', label: 'Text label', key: 'L', icon: Type },
];

const SHORTCUTS: [string, string][] = [
    ['V H R B S C T P E L', 'Switch tool'],
    ['Space + drag', 'Pan'],
    ['Ctrl + scroll', 'Zoom'],
    ['Shift 1 / Shift 2 / Shift 0', 'Fit / Selection / 100%'],
    ['Alt + click', 'Select single seat'],
    ['Shift + click', 'Add to selection'],
    ['Arrows (Shift)', 'Nudge 1px (10px)'],
    ['1 to 9, 0', 'Assign tier, clear'],
    ['Ctrl C / X / V', 'Copy / Cut / Paste'],
    ['Ctrl D', 'Duplicate'],
    ['Ctrl A', 'Select all'],
    ['Del', 'Delete'],
    ['Ctrl Z / Ctrl Shift Z', 'Undo / Redo'],
    ['Ctrl S', 'Save'],
];

const isTyping = (t: EventTarget | null) => {
    const el = t as HTMLElement | null;
    return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
};

const selectionRect = (plan: Plan, index: PlanIndex, sel: Selection): Rect | null => {
    const pts: Point[] = [];
    sel.seats.forEach(k => {
        const ref = index.seats.get(k);
        if (!ref) return;
        const zone = plan.zones[ref.zi];
        const row = zone.rows[ref.ri];
        pts.push(seatAbs(zone, row, row.seats[ref.si]));
    });
    sel.shapes.forEach(k => {
        const ref = index.shapes.get(k);
        if (!ref) return;
        const b = shapeBounds(ref.kind === 'stage' ? plan.stages![ref.index] : plan.entrances![ref.index]);
        pts.push({ x: b.x, y: b.y }, { x: b.x + b.width, y: b.y + b.height });
    });
    return boundsOf(pts, sel.seats.size ? SEAT_RADIUS : 0);
};

const nextRowLabel = (zone: Zone, nth: number, scheme: EditorSettings['rowScheme']) => {
    const labels = zone.rows.map(r => r.row_number);
    if (scheme === 'numeric') return String(nextNumericLabel(labels, 1) + nth);
    return makeLabel(zone.rows.length + nth, scheme);
};

const cloneInto = (
    target: Plan,
    source: Plan,
    sel: Selection,
    offset: Point,
    zoneIndex: number,
    rowScheme: EditorSettings['rowScheme'],
): { plan: Plan; selection: Selection } => {
    const sections = [...fullZoneIndexes(source, sel.seats)].filter(zi => source.zones[zi].section);
    const sectionSeats = new Set(sections.flatMap(zi => zoneSeatKeys(source.zones[zi])));
    const dup = duplicateSections(target, source, sections, offset);
    const rest: Selection = { seats: new Set([...sel.seats].filter(k => !sectionSeats.has(k))), shapes: sel.shapes };
    if (!rest.seats.size && !rest.shapes.size) return { plan: dup.plan, selection: { seats: dup.seats, shapes: new Set() } };
    const free = ensureFreeZone(dup.plan, zoneIndex);
    const result = pasteSelection(free.plan, source, rest, offset, {
        zoneIndex: free.zi,
        rowLabelFor: (zone, nth) => nextRowLabel(zone, nth, rowScheme),
    });
    return { plan: result.plan, selection: { seats: new Set([...dup.seats, ...result.selection.seats]), shapes: result.selection.shapes } };
};

const seatKeysOf = (plan: Plan): Set<string> => {
    const keys = new Set<string>();
    plan.zones.forEach(z => z.rows.forEach(r => r.seats.forEach(s => keys.add(seatKey(s)))));
    return keys;
};

const SeatMapStudioInner: React.FC<SeatMapStudioProps> = ({ initialPlan, onSave, onBack, title, subtitle, showExport = true, lockedSeatsMessage, editingLockedMessage }) => {
    const dialogs = useDialogs();
    const lastLockedToast = useRef(0);
    const lockedKeys = useRef<Set<string>>(new Set());
    const removesLockedSeat = useCallback((next: Plan) => {
        if (!lockedKeys.current.size) return false;
        const kept = seatKeysOf(next);
        for (const key of lockedKeys.current) if (!kept.has(key)) return true;
        return false;
    }, []);
    const { plan, commit, undo, redo, reset, canUndo, canRedo } = usePlanHistory(
        () => (initialPlan ? normalizePlan(initialPlan) : emptyPlan()),
        (_current, next) => {
            if (editingLockedMessage) {
                if (Date.now() - lastLockedToast.current > 3000) {
                    lastLockedToast.current = Date.now();
                    addToast({ title: 'Editing locked', description: editingLockedMessage, color: 'warning' });
                }
                return false;
            }
            if (!removesLockedSeat(next)) return true;
            addToast({ title: 'Seats cannot be deleted', description: lockedSeatsMessage || 'Tickets have been sold for these seats. You can add or move seats.', color: 'warning' });
            return false;
        },
    );
    const [savedPlan, setSavedPlan] = useState<Plan>(plan);
    useEffect(() => {
        lockedKeys.current = lockedSeatsMessage ? seatKeysOf(savedPlan) : new Set();
    }, [lockedSeatsMessage, savedPlan]);
    const [saving, setSaving] = useState(false);
    const [rawSelection, setSelection] = useState<Selection>(EMPTY);
    const [tool, setTool] = useState<Tool>('select');
    const [settings, setSettings] = useState<EditorSettings>(loadSettings);
    const [activeZone, setActiveZone] = useState(0);
    const [spaceDown, setSpaceDown] = useState(false);
    const [showShortcuts, setShowShortcuts] = useState(false);
    const [focusSignal, setFocusSignal] = useState(0);
    const compact = useIsMobile({ breakpoint: 1023, includeTablet: true });
    const [mobilePanel, setMobilePanel] = useState<'layers' | 'inspector' | null>(null);
    const [showTemplates, setShowTemplates] = useState(() => !plan.zones.some(z => z.rows.length) && !plan.stages?.length);
    const canvasRef = useRef<CanvasHandle>(null);
    const clipboard = useRef<{ plan: Plan; selection: Selection; pastes: number } | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    const index = useMemo(() => buildIndex(plan), [plan]);
    const selection = useMemo<Selection>(() => {
        const seats = [...rawSelection.seats].filter(k => index.seats.has(k));
        const shapes = [...rawSelection.shapes].filter(k => index.shapes.has(k));
        if (seats.length === rawSelection.seats.size && shapes.length === rawSelection.shapes.size) return rawSelection;
        return { seats: new Set(seats), shapes: new Set(shapes) };
    }, [rawSelection, index]);
    const hasSelection = selection.seats.size > 0 || selection.shapes.size > 0;
    const dirty = plan !== savedPlan;
    const zoneIndex = Math.min(activeZone, plan.zones.length - 1);

    useEffect(() => {
        try {
            localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
        } catch {
            /* storage unavailable */
        }
    }, [settings]);

    useEffect(() => {
        if (!dirty) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [dirty]);

    const updateSettings = useCallback((patch: Partial<EditorSettings>) => setSettings(s => ({ ...s, ...patch })), []);

    const select = useCallback((sel: Selection, additive = false) => {
        const linked = linkTables(plan, index, sel);
        if (!additive) {
            setSelection(linked);
            return;
        }
        setSelection(prev => ({
            seats: new Set([...prev.seats, ...linked.seats]),
            shapes: new Set([...prev.shapes, ...linked.shapes]),
        }));
    }, [plan, index]);

    const selectAndFocus = useCallback((sel: Selection, additive = false) => {
        select(sel, additive);
        setFocusSignal(n => n + 1);
    }, [select]);

    const handleMove = useCallback((d: Point) => commit(p => moveSelection(p, buildIndex(p), selection, d)), [commit, selection]);

    const handleRotate = useCallback(
        (center: Point, deg: number, mergeKey?: string) => commit(p => rotateSelection(p, selection, center, deg), mergeKey),
        [commit, selection],
    );

    const rotateBy = useCallback((deg: number, mergeKey?: string) => {
        const r = selectionRect(plan, index, selection);
        if (r) handleRotate({ x: r.x + r.width / 2, y: r.y + r.height / 2 }, deg, mergeKey);
    }, [plan, index, selection, handleRotate]);

    const handleResizeShape = useCallback((key: string, rect: Rect) => {
        commit(p => {
            const ref = buildIndex(p).shapes.get(key);
            if (!ref) return p;
            const s = ref.kind === 'stage' ? p.stages![ref.index] : p.entrances![ref.index];
            const scaled = scaleShape(s, rect);
            return updateShape(p, key, { position: scaled.position, size: scaled.size, points: scaled.points });
        });
    }, [commit]);

    const handleResizeTable = useCallback((key: string, rect: Rect) => {
        commit(p => {
            const stage = p.stages?.find(st => `stage:${st.id}` === key);
            if (!stage?.tableRow) return p;
            return reshapeTable(p, stage.tableRow, stage.id, {
                size: { width: Math.round(rect.width), height: Math.round(rect.height) },
                center: { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 },
                spacing: settings.seatSpacing,
                seatScheme: 'numeric',
            });
        });
    }, [commit, settings.seatSpacing]);

    const handleDrawRows = useCallback((rowsPoints: Point[][]) => {
        const { plan: base, zi } = ensureFreeZone(plan, zoneIndex);
        const zone = base.zones[zi];
        const rows = rowsPoints.map((pts, i) => buildRow(zone, pts, nextRowLabel(zone, i, settings.rowScheme), settings.seatScheme));
        commit(() => addRows(base, zi, rows));
        setSelection({ seats: new Set(rows.flatMap(r => r.seats.map(seatKey))), shapes: new Set() });
    }, [plan, zoneIndex, settings.rowScheme, settings.seatScheme, commit]);

    const handleDrawShape = useCallback((kind: ShapeKind, rect: Rect) => {
        const count = (kind === 'stage' ? plan.stages : plan.entrances)?.length ?? 0;
        const shape = newShape(kind, rect, kind === 'stage' ? (count ? `Stage ${count + 1}` : 'Stage') : `Entrance ${count + 1}`);
        commit(p => addShape(p, kind, shape));
        setSelection({ seats: new Set(), shapes: new Set([shapeKey(kind, shape.id)]) });
        setTool('select');
    }, [plan, commit]);

    const handleDrawPolygon = useCallback((points: Point[]) => {
        const shape = polygonShape(points, 'Area');
        commit(p => addShape(p, 'stage', shape));
        setSelection({ seats: new Set(), shapes: new Set([shapeKey('stage', shape.id)]) });
        setTool('select');
    }, [commit]);

    const handlePlace = useCallback((kind: 'table' | 'text', p: Point) => {
        if (kind === 'text') {
            const shape = { ...newShape('stage', { x: p.x - 60, y: p.y - 15, width: 120, height: 30 }, 'Label'), color: 'transparent', strokeColor: 'transparent', strokeWidth: 0, curveRadius: 0, variant: 'text' as const };
            commit(pl => addShape(pl, 'stage', shape));
            setSelection({ seats: new Set(), shapes: new Set([shapeKey('stage', shape.id)]) });
            setTool('select');
            return;
        }
        const { plan: base, zi } = ensureFreeZone(plan, zoneIndex);
        const { row, stage } = createTable(base.zones[zi], p, {
            count: settings.tableSeats,
            shape: settings.tableShape,
            spacing: settings.seatSpacing,
            label: nextItemLabel(plan),
            seatScheme: 'numeric',
            endSeats: settings.tableEndSeats,
        });
        commit(() => addShape(addRows(base, zi, [row]), 'stage', stage));
        setSelection({ seats: new Set(row.seats.map(seatKey)), shapes: new Set([shapeKey('stage', stage.id)]) });
    }, [plan, zoneIndex, settings, commit]);

    const handleCreate = useCallback((req: CreateRequest) => {
        const origin = snapPoint(canvasRef.current?.viewCenter() ?? { x: 0, y: 0 }, settings.gridSize);
        const build = (c: Point): { plan: Plan; selection: Selection; bounds: Rect | null } => {
            if (req.kind === 'section') {
                const zone = createSectionZone(req.name, c, {
                    rows: req.rows,
                    seatsPerRow: req.seats,
                    seatSpacing: settings.seatSpacing,
                    rowSpacing: settings.rowSpacing,
                    labels: {
                        rowScheme: settings.rowScheme,
                        rowStart: defaultStartFor(settings.rowScheme),
                        seatScheme: settings.seatScheme,
                        seatStart: defaultStartFor(settings.seatScheme),
                    },
                });
                return { plan: { ...plan, zones: [...plan.zones, zone] }, selection: { seats: new Set(zoneSeatKeys(zone)), shapes: new Set() }, bounds: zoneBounds(zone) };
            }
            if (req.kind === 'table') {
                const { plan: base, zi } = ensureFreeZone(plan, zoneIndex);
                const zone = base.zones[zi];
                const { row, stage } = createTable(zone, c, {
                    count: req.seats,
                    shape: req.shape,
                    spacing: settings.seatSpacing,
                    label: req.name,
                    seatScheme: 'numeric',
                    endSeats: req.endSeats,
                });
                return {
                    plan: addShape(addRows(base, zi, [row]), 'stage', stage),
                    selection: { seats: new Set(row.seats.map(seatKey)), shapes: new Set([shapeKey('stage', stage.id)]) },
                    bounds: boundsOf(row.seats.map(st => seatAbs(zone, row, st)), SEAT_RADIUS + 4),
                };
            }
            let shape;
            if (req.kind === 'ga') shape = createGaArea(c, req.name, req);
            else if (req.kind === 'object') shape = createObject(c, req);
            else {
                const width = Math.max(60, Math.round(req.text.length * req.fontSize * 0.62 + 24));
                const height = Math.round(req.fontSize * 2);
                shape = {
                    ...newShape('stage', { x: c.x - width / 2, y: c.y - height / 2, width, height }, req.text),
                    color: 'transparent', strokeColor: 'transparent', strokeWidth: 0, curveRadius: 0, variant: 'text' as const, fontSize: req.fontSize,
                };
            }
            return { plan: addShape(plan, 'stage', shape), selection: { seats: new Set(), shapes: new Set([shapeKey('stage', shape.id)]) }, bounds: shapeBounds(shape) };
        };
        let item = build(origin);
        if (item.bounds) {
            const d = findFreeOffset(plan, item.bounds);
            if (d.x || d.y) item = build(add(origin, d));
        }
        const next = item.plan;
        commit(() => next);
        setSelection(item.selection);
        canvasRef.current?.revealSelection();
        setTool('select');
    }, [plan, zoneIndex, settings, commit]);

    const handleCopy = useCallback((cut: boolean) => {
        if (!hasSelection) return;
        clipboard.current = { plan, selection, pastes: 0 };
        if (cut) {
            commit(p => deleteSelection(p, selection));
            setSelection(EMPTY);
        }
    }, [plan, selection, hasSelection, commit]);

    const handlePaste = useCallback(() => {
        const clip = clipboard.current;
        if (!clip) return;
        clip.pastes += 1;
        const step = settings.gridSize * 4 * clip.pastes;
        const result = cloneInto(plan, clip.plan, clip.selection, { x: step, y: step }, zoneIndex, settings.rowScheme);
        commit(() => result.plan);
        setSelection(result.selection);
    }, [plan, zoneIndex, settings, commit]);

    const handleDelete = useCallback(() => {
        if (!hasSelection) return;
        commit(p => deleteSelection(p, selection));
        setSelection(EMPTY);
    }, [commit, selection, hasSelection]);

    const handleDuplicate = useCallback(() => {
        if (!hasSelection) return;
        const r = selectionRect(plan, index, selection);
        let offset = r && selection.seats.size
            ? { x: 0, y: Math.round(r.height + settings.rowSpacing - SEAT_RADIUS * 2) }
            : { x: settings.gridSize * 4, y: settings.gridSize * 4 };
        if (r && fullZoneIndexes(plan, selection.seats).size) {
            const extra = findFreeOffset(plan, { ...r, x: r.x + offset.x, y: r.y + offset.y });
            offset = add(offset, extra);
        }
        const result = cloneInto(plan, plan, selection, offset, zoneIndex, settings.rowScheme);
        commit(() => result.plan);
        setSelection(result.selection);
    }, [plan, index, selection, hasSelection, settings, zoneIndex, commit]);

    const handleSave = useCallback(async () => {
        if (!onSave || saving || editingLockedMessage) return;
        const snapshot = plan;
        try {
            setSaving(true);
            await onSave(snapshot);
            setSavedPlan(snapshot);
        } catch {
            /* caller reports errors */
        } finally {
            setSaving(false);
        }
    }, [onSave, plan, saving, editingLockedMessage]);

    const handleExport = useCallback(() => {
        const blob = new Blob([JSON.stringify(plan, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(plan.name || 'seatmap').replace(/[^\w-]+/g, '_')}.json`;
        a.click();
        URL.revokeObjectURL(url);
    }, [plan]);

    const handleImport = useCallback(async (file: File) => {
        try {
            if (editingLockedMessage) {
                await dialogs.alert(editingLockedMessage);
                return;
            }
            const parsed = JSON.parse(await file.text());
            if (!parsed || !Array.isArray(parsed.zones)) throw new Error('missing zones');
            if (dirty && !(await dialogs.confirm({ title: 'Replace seat map', message: 'Replace the current seat map with the imported file? Unsaved changes will be lost.', confirmLabel: 'Replace', destructive: true }))) return;
            const imported = normalizePlan(parsed);
            if (removesLockedSeat(imported)) {
                await dialogs.alert(lockedSeatsMessage || 'This file is missing seats that already have tickets sold. You can add or move seats, but not delete them.');
                return;
            }
            reset(imported);
            setSelection(EMPTY);
            setActiveZone(0);
            requestAnimationFrame(() => canvasRef.current?.fit());
        } catch {
            await dialogs.alert('This file is not a valid seat map JSON.');
        }
    }, [dirty, reset, removesLockedSeat, lockedSeatsMessage, editingLockedMessage, dialogs]);

    const applyTemplate = useCallback(async (next: Plan) => {
        const hasContent = plan.zones.some(z => z.rows.length) || !!plan.stages?.length || !!plan.entrances?.length;
        if (hasContent && !(await dialogs.confirm({ title: 'Replace layout', message: 'Replace the current layout with this template? You can undo this.', confirmLabel: 'Replace' }))) return;
        commit(() => ({ ...next, name: plan.name && plan.name !== 'New Seat Map' ? plan.name : next.name }));
        setSelection(EMPTY);
        setActiveZone(0);
        setShowTemplates(false);
        requestAnimationFrame(() => canvasRef.current?.fit());
    }, [plan, commit, dialogs]);

    const handleAddZone = useCallback(() => {
        commit(p => ({
            ...p,
            zones: [...p.zones, { name: `Zone ${p.zones.length + 1}`, code: `Z${p.zones.length + 1}`, position: { x: 0, y: 0 }, rows: [] }],
        }));
        setActiveZone(plan.zones.length);
    }, [commit, plan.zones.length]);

    const handleUpdateZone = useCallback((zi: number, patch: Partial<Zone>) => {
        commit(p => ({ ...p, zones: p.zones.map((z, i) => (i === zi ? { ...z, ...patch } : z)) }));
    }, [commit]);

    const handleDeleteZone = useCallback((zi: number) => {
        commit(p => (p.zones.length > 1 ? { ...p, zones: p.zones.filter((_, i) => i !== zi) } : p));
        setActiveZone(z => Math.max(0, z >= zi ? z - 1 : z));
    }, [commit]);

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (isTyping(e.target) || showTemplates) return;
            const mod = e.metaKey || e.ctrlKey;
            const key = e.key.toLowerCase();

            if (e.code === 'Space') {
                e.preventDefault();
                setSpaceDown(true);
                return;
            }
            if (mod && key === 'z') {
                e.preventDefault();
                if (e.shiftKey) redo();
                else undo();
                return;
            }
            if (mod && key === 'y') {
                e.preventDefault();
                redo();
                return;
            }
            if (mod && key === 's') {
                e.preventDefault();
                handleSave();
                return;
            }
            if (mod && (key === 'c' || key === 'x')) {
                if (window.getSelection()?.toString()) return;
                e.preventDefault();
                handleCopy(key === 'x');
                return;
            }
            if (mod && key === 'v') {
                e.preventDefault();
                handlePaste();
                return;
            }
            if (mod && key === 'd') {
                e.preventDefault();
                handleDuplicate();
                return;
            }
            if (mod && key === 'a') {
                e.preventDefault();
                setSelection({
                    seats: new Set(index.seats.keys()),
                    shapes: new Set(index.shapes.keys()),
                });
                return;
            }
            if (mod && (key === '=' || key === '+')) {
                e.preventDefault();
                canvasRef.current?.zoomBy(1.25);
                return;
            }
            if (mod && key === '-') {
                e.preventDefault();
                canvasRef.current?.zoomBy(0.8);
                return;
            }
            if (mod) return;

            if (e.shiftKey && e.code === 'Digit1') {
                canvasRef.current?.fit();
                return;
            }
            if (e.shiftKey && e.code === 'Digit2') {
                canvasRef.current?.zoomToSelection();
                return;
            }
            if (e.shiftKey && e.code === 'Digit0') {
                canvasRef.current?.resetZoom();
                return;
            }
            if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault();
                handleDelete();
                return;
            }
            if (e.key === 'Escape') {
                setSelection(EMPTY);
                setTool('select');
                setShowShortcuts(false);
                return;
            }
            if (e.key.startsWith('Arrow') && hasSelection) {
                e.preventDefault();
                const step = e.shiftKey ? 10 : 1;
                const d = {
                    x: e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0,
                    y: e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0,
                };
                commit(p => moveSelection(p, buildIndex(p), selection, d), 'nudge');
                return;
            }
            if (/^[0-9]$/.test(e.key) && selection.seats.size) {
                const n = Number(e.key);
                const cat = n === 0 ? '' : plan.categories[n - 1]?.name;
                if (cat !== undefined) commit(p => setSeatCategory(p, selection.seats, cat));
                return;
            }
            if (e.key === '?') {
                setShowShortcuts(s => !s);
                return;
            }
            const t = TOOLS.find(x => x.key.toLowerCase() === key);
            if (t && !e.altKey) setTool(t.id);
        };
        const onKeyUp = (e: KeyboardEvent) => {
            if (e.code === 'Space') setSpaceDown(false);
        };
        const onBlur = () => setSpaceDown(false);
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('keyup', onKeyUp);
        window.addEventListener('blur', onBlur);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('keyup', onKeyUp);
            window.removeEventListener('blur', onBlur);
        };
    }, [showTemplates, undo, redo, handleSave, handleDuplicate, handleDelete, handleCopy, handlePaste, commit, selection, hasSelection, index, plan.categories]);

    const toolButtons = TOOLS.map(t => {
        const Icon = t.icon;
        return (
            <React.Fragment key={t.id}>
                {(t.id === 'row' || t.id === 'stage') && <Divider />}
                <IconButton label={t.label} shortcut={compact ? undefined : t.key} active={tool === t.id} onClick={() => setTool(t.id)} className="shrink-0">
                    <Icon className="h-4 w-4" />
                </IconButton>
            </React.Fragment>
        );
    });

    const fileInput = (
        <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={e => {
                const f = e.target.files?.[0];
                if (f) handleImport(f);
                e.target.value = '';
            }}
        />
    );

    const saveButton = onSave && (
        <button
            type="button"
            onClick={handleSave}
            disabled={saving || !dirty || !!editingLockedMessage}
            title={editingLockedMessage ? 'Editing is locked' : 'Save (Ctrl S)'}
            className={cx(
                'inline-flex h-8 shrink-0 items-center gap-1.5 rounded px-3 text-xs font-semibold transition-colors',
                compact ? 'ml-1' : 'ml-2',
                dirty
                    ? 'bg-primary40 text-white hover:bg-primary30 dark:bg-nightAccent dark:text-nightBg'
                    : 'bg-gray-100 text-gray-400 dark:bg-nightElevated dark:text-nightMuted',
            )}
        >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {saving ? 'Saving' : dirty ? 'Save' : 'Saved'}
        </button>
    );

    const titleBlock = (
        <div className="min-w-0">
            <div className="flex items-center gap-2">
                <h1 className="truncate text-sm font-semibold">{title || plan.name || 'Seat map'}</h1>
                {dirty && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-warning50" title="Unsaved changes" />}
                {editingLockedMessage ? (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-500/15 dark:text-amber-300" title={editingLockedMessage}>
                        <Lock className="h-3 w-3" /> Editing locked
                    </span>
                ) : lockedSeatsMessage && (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-500/15 dark:text-amber-300" title={lockedSeatsMessage}>
                        <Lock className="h-3 w-3" /> Seats locked
                    </span>
                )}
            </div>
            {subtitle && <p className="truncate text-[11px] text-gray-500 dark:text-nightMuted">{subtitle}</p>}
        </div>
    );

    const layersPanelProps = {
        plan,
        selection,
        activeZone: zoneIndex,
        onActiveZone: setActiveZone,
        onAddZone: handleAddZone,
        onUpdateZone: handleUpdateZone,
        onDeleteZone: handleDeleteZone,
    };

    const inspectorProps = {
        plan,
        index,
        selection,
        settings,
        tool,
        onSettings: updateSettings,
        commit,
        onSelect: selectAndFocus,
        onKeepSelection: select,
        onDelete: handleDelete,
        onDuplicate: handleDuplicate,
        onRotate: rotateBy,
        onCreate: handleCreate,
        lockedMessage: editingLockedMessage,
    };

    const selectionSummary = [
        selection.seats.size && `${selection.seats.size} seat${selection.seats.size === 1 ? '' : 's'}`,
        selection.shapes.size && `${selection.shapes.size} object${selection.shapes.size === 1 ? '' : 's'}`,
    ].filter(Boolean).join(', ');

    return (
        <div className="flex h-full min-h-0 flex-col bg-white text-gray-900 dark:bg-nightBg dark:text-nightTextSecondary">
            {compact ? (
                <header className="shrink-0 border-b border-gray-200 dark:border-nightBorder dark:bg-nightSurface" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
                    <div className="flex h-12 items-center gap-1 px-2">
                        {onBack && (
                            <IconButton label="Back" onClick={onBack} className="shrink-0">
                                <ArrowLeft className="h-4 w-4" />
                            </IconButton>
                        )}
                        <div className="min-w-0 flex-1 px-1">{titleBlock}</div>
                        <IconButton label="Undo" onClick={undo} disabled={!canUndo} className="shrink-0">
                            <Undo2 className="h-4 w-4" />
                        </IconButton>
                        <IconButton label="Redo" onClick={redo} disabled={!canRedo} className="shrink-0">
                            <Redo2 className="h-4 w-4" />
                        </IconButton>
                        {saveButton}
                    </div>
                    <div className="flex h-11 items-center gap-0.5 overflow-x-auto border-t border-gray-100 px-2 dark:border-nightBorder [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        <IconButton label="Zones and rows" active={mobilePanel === 'layers'} onClick={() => setMobilePanel(p => (p === 'layers' ? null : 'layers'))} className="shrink-0">
                            <Layers className="h-4 w-4" />
                        </IconButton>
                        <Divider />
                        {toolButtons}
                        <Divider />
                        <IconButton label="Templates" onClick={() => setShowTemplates(true)} className="shrink-0">
                            <LayoutTemplate className="h-4 w-4" />
                        </IconButton>
                        <IconButton label="Snap to grid" active={settings.snap} onClick={() => updateSettings({ snap: !settings.snap })} className="shrink-0">
                            <Magnet className="h-4 w-4" />
                        </IconButton>
                        <IconButton label="Import JSON" onClick={() => fileRef.current?.click()} className="shrink-0">
                            <Upload className="h-4 w-4" />
                        </IconButton>
                        {showExport && (
                            <IconButton label="Export JSON" onClick={handleExport} className="shrink-0">
                                <Download className="h-4 w-4" />
                            </IconButton>
                        )}
                        <IconButton label="Properties" active={mobilePanel === 'inspector'} onClick={() => setMobilePanel(p => (p === 'inspector' ? null : 'inspector'))} className="shrink-0">
                            <SlidersHorizontal className="h-4 w-4" />
                        </IconButton>
                    </div>
                    {fileInput}
                </header>
            ) : (
                <header className="flex h-12 shrink-0 items-center gap-2 border-b border-gray-200 px-3 dark:border-nightBorder dark:bg-nightSurface">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                        {onBack && (
                            <IconButton label="Back" onClick={onBack}>
                                <ArrowLeft className="h-4 w-4" />
                            </IconButton>
                        )}
                        {titleBlock}
                    </div>

                    <nav className="flex items-center gap-0.5 rounded-lg border border-gray-200 bg-gray-50 p-0.5 dark:border-nightBorder dark:bg-nightElevated">
                        {toolButtons}
                    </nav>

                    <div className="flex flex-1 items-center justify-end gap-0.5">
                        <button
                            type="button"
                            onClick={() => setShowTemplates(true)}
                            className="mr-1 inline-flex h-8 items-center gap-1.5 rounded px-2.5 text-xs font-medium text-gray-700 hover:bg-gray-100 dark:text-nightTextSecondary dark:hover:bg-nightElevated"
                        >
                            <LayoutTemplate className="h-4 w-4" /> Templates
                        </button>
                        <Divider />
                        <IconButton label="Undo" shortcut="Ctrl Z" onClick={undo} disabled={!canUndo}>
                            <Undo2 className="h-4 w-4" />
                        </IconButton>
                        <IconButton label="Redo" shortcut="Ctrl Shift Z" onClick={redo} disabled={!canRedo}>
                            <Redo2 className="h-4 w-4" />
                        </IconButton>
                        <Divider />
                        <IconButton label="Snap to grid" active={settings.snap} onClick={() => updateSettings({ snap: !settings.snap })}>
                            <Magnet className="h-4 w-4" />
                        </IconButton>
                        <IconButton label="Keyboard shortcuts" shortcut="?" active={showShortcuts} onClick={() => setShowShortcuts(s => !s)}>
                            <Keyboard className="h-4 w-4" />
                        </IconButton>
                        <Divider />
                        <IconButton label="Import JSON" onClick={() => fileRef.current?.click()}>
                            <Upload className="h-4 w-4" />
                        </IconButton>
                        {showExport && (
                            <IconButton label="Export JSON" onClick={handleExport}>
                                <Download className="h-4 w-4" />
                            </IconButton>
                        )}
                        {fileInput}
                        {saveButton}
                    </div>
                </header>
            )}

            <div className="relative flex min-h-0 flex-1">
                {!compact && <LayersPanel {...layersPanelProps} onSelect={selectAndFocus} />}

                <Canvas
                    ref={canvasRef}
                    plan={plan}
                    index={index}
                    selection={selection}
                    tool={tool}
                    settings={settings}
                    spaceDown={spaceDown}
                    focusSignal={focusSignal}
                    onSelect={select}
                    onMove={handleMove}
                    onRotate={handleRotate}
                    onResizeShape={handleResizeShape}
                    onResizeTable={handleResizeTable}
                    onDrawRows={handleDrawRows}
                    onDrawShape={handleDrawShape}
                    onDrawPolygon={handleDrawPolygon}
                    onPlace={handlePlace}
                />

                {!compact && <Inspector {...inspectorProps} />}

                {compact && mobilePanel && (
                    <>
                        <div className="absolute inset-0 z-30 bg-black/40" onClick={() => setMobilePanel(null)} />
                        {mobilePanel === 'layers' ? (
                            <div className="absolute inset-y-0 left-0 z-40 flex w-[85%] max-w-xs flex-col shadow-2xl">
                                <LayersPanel
                                    {...layersPanelProps}
                                    className="h-full w-full"
                                    onSelect={(sel, additive) => {
                                        selectAndFocus(sel, additive);
                                        setMobilePanel(null);
                                    }}
                                />
                            </div>
                        ) : (
                            <div
                                className="absolute inset-x-0 bottom-0 z-40 flex max-h-[78%] flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl dark:bg-nightSurface"
                                style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
                            >
                                <button type="button" aria-label="Close properties" onClick={() => setMobilePanel(null)} className="flex shrink-0 justify-center py-2">
                                    <span className="h-1 w-10 rounded-full bg-gray-300 dark:bg-nightBorder" />
                                </button>
                                <Inspector {...inspectorProps} className="min-h-0 w-full flex-1" />
                            </div>
                        )}
                    </>
                )}

                {compact && !mobilePanel && hasSelection && (
                    <button
                        type="button"
                        onClick={() => setMobilePanel('inspector')}
                        className="absolute bottom-16 left-1/2 z-20 inline-flex h-10 -translate-x-1/2 items-center gap-2 rounded-full bg-primary40 px-4 text-sm font-semibold text-white shadow-lg dark:bg-nightAccent dark:text-nightBg"
                    >
                        <SlidersHorizontal className="h-4 w-4" /> Edit {selectionSummary}
                    </button>
                )}

                {showTemplates && (
                    <TemplatesModal
                        onPick={applyTemplate}
                        onBlank={() => setShowTemplates(false)}
                        onClose={() => setShowTemplates(false)}
                    />
                )}

                {showShortcuts && !compact && (
                    <div className="absolute right-[19rem] top-3 z-10 w-72 rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-nightBorder dark:bg-nightElevated">
                        <div className="mb-2 text-xs font-semibold">Keyboard shortcuts</div>
                        <dl className="space-y-1">
                            {SHORTCUTS.map(([k, v]) => (
                                <div key={k} className="flex items-center justify-between gap-3 text-[11px]">
                                    <dt className="font-mono text-gray-700 dark:text-nightTextSecondary">{k}</dt>
                                    <dd className="text-right text-gray-500 dark:text-nightMuted">{v}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                )}
            </div>
        </div>
    );
};

const SeatMapStudio: React.FC<SeatMapStudioProps> = props => (
    <DialogProvider>
        <SeatMapStudioInner {...props} />
    </DialogProvider>
);

export default SeatMapStudio;
